import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from './theme';
import { App } from './App';
import { AuthProvider } from './state/AuthContext';
import { I18nProvider } from './state/I18nContext';
import { PublicTournamentView } from './ui/public/PublicTournamentView';
import { PublicProfileView } from './ui/public/PublicProfileView';
import { PublicClubView } from './ui/public/PublicClubView';
import { parsePublicViewPath } from './ui/public/sharePath';
import { parsePublicProfilePath } from './ui/public/profileSharePath';
import { parsePublicClubPath } from './ui/public/clubSharePath';
import { isSignupPath } from './ui/public/signupPath';
import { SignupWizard } from './ui/public/SignupWizard';
import { ClaimScreen } from './ui/ClaimScreen';
import { readClaimTokenFromUrl } from './data/authClient';

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root not found');

// A `/view/:id` path is a public share link: render the standalone, read-only,
// mobile-first spectator view with no authentication. Every other path is the
// auth-gated admin app. The id is read once at load — the public view does not
// need in-app navigation.
//
// Both trees are internationalised (see `features/platform-i18n.md`): the
// public view resolves its language from the device locale, the admin app
// additionally from the signed-in user's company default.
const publicViewId = parsePublicViewPath(window.location.pathname);
// A `/p/:token` path is a public athlete/coach profile share link, resolved the
// same way as the tournament view: standalone, read-only, no authentication.
const publicProfileToken = parsePublicProfilePath(window.location.pathname);
// A `/club/:token` path is a public sport club home page share link — the same
// standalone, read-only, unauthenticated model as the two views above.
const publicClubToken = parsePublicClubPath(window.location.pathname);
// A `/claim?token=…` path is an emailed athlete-access invitation (see
// `features/athlete-access.md`). Like the views above it renders outside
// `AuthProvider` — the recipient has no session yet, and the token in the URL is
// the entire capability. It is NOT redeemed on load: the screen waits for an
// explicit click so an email link-scanner cannot burn the single-use invitation.
const claimToken = readClaimTokenFromUrl();
// A `/signup` path is the public "Sign up your sport club" wizard (see
// `features/company-registration.md`) — unauthenticated like the views above:
// the visitor has no account until the wizard's final step creates one.
const isSignup = isSignupPath(window.location.pathname);

createRoot(container).render(
  <StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <I18nProvider>
        {publicViewId !== null ? (
          <PublicTournamentView tournamentId={publicViewId} />
        ) : publicProfileToken !== null ? (
          <PublicProfileView profileToken={publicProfileToken} />
        ) : publicClubToken !== null ? (
          <PublicClubView clubToken={publicClubToken} />
        ) : claimToken !== null ? (
          <ClaimScreen token={claimToken} />
        ) : isSignup ? (
          <SignupWizard />
        ) : (
          <AuthProvider>
            <App />
          </AuthProvider>
        )}
      </I18nProvider>
    </ThemeProvider>
  </StrictMode>,
);
